/**
 * loading-skeletons — the shared ghost vocabulary for customer surfaces.
 *
 * Every shape is the design system's `Skeleton`. jrni-ui owns the bar, its
 * neutral steps and its sweep; app code must not re-implement them.
 *
 * Dimensions and radii are AUTHORED from the surface each ghost stands in for —
 * read off its source, not measured against a rendered screen. They are meant
 * to keep the swap small, and they are close rather than exact. No seed data: a
 * ghost is a shape.
 */
import type { CSSProperties, JSX, ReactNode } from "react";
import { Skeleton } from "@v0y4ge/jrni-ui";

/**
 * The class that scopes the stylesheet below. It goes on the element that
 * CONTAINS ghosts: `LoadingRegion` puts it on its own root, and a surface whose
 * ghosts are spread through real chrome (the One Stop board) puts it on each
 * region it already renders and mounts `GhostStyles` once.
 */
export const GHOST_CLASS = "vg-ghost";

/**
 * The two things the DS component cannot be told, as a scoped stylesheet rather
 * than a fork of it.
 *
 * `Skeleton` writes both its animation and its corner as INLINE styles
 * (`packages/jrni-ui/src/Skeleton.tsx`) and exposes neither as a prop — only
 * `shimmer`, a render-time boolean, which cannot answer a media query. An
 * author declaration marked `!important` is what outranks an inline one, and
 * rendering the rules in the tree rather than injecting them from an effect is
 * what makes them apply on the first paint.
 *
 *   · REDUCED MOTION — the sweep stops and the bar settles on the same flat
 *     neutral the DS itself renders for `shimmer={false}`.
 *   · CORNER — a wrapper that only CLIPS can round a corner but can never
 *     un-round one, so an authored square (`radius="0px"` — what a journey
 *     configured at `radiusScale: 0` hands down) still showed the DS element's
 *     own `--jrni-radius-sm`. Inheriting the wrapper's radius makes the
 *     authored value the one that renders, whatever it is.
 */
const GHOST_CSS =
  "." + GHOST_CLASS + " [data-vg-ghost-radius] > [data-jrni-component=\"skeleton\"]" +
  "{border-radius:inherit!important}" +
  "@media (prefers-reduced-motion: reduce){." + GHOST_CLASS +
  " [data-jrni-component=\"skeleton\"]{animation:none!important;" +
  "background-image:none!important;background-color:var(--jrni-color-neutral-5)!important}}";

/**
 * The scoped stylesheet, mounted synchronously in the tree.
 *
 * `LoadingRegion` renders it for the common case. A surface that scopes itself
 * renders it once, beside whatever else it mounts its theme with. Repeats are
 * harmless — the rules are idempotent — but one per surface is enough, and a
 * surface that renders NEITHER gets no reduced-motion handling and no authored
 * corner: the ghosts fall back to the DS defaults.
 */
export function GhostStyles(): JSX.Element {
  return <style>{GHOST_CSS}</style>;
}

/** Visually hidden. An inline style, not a class, so the one part assistive
 *  tech depends on cannot fail because a stylesheet did not mount. */
export const SR_ONLY: CSSProperties = {
  position: "absolute", width: 1, height: 1, margin: -1, padding: 0,
  overflow: "hidden", clip: "rect(0 0 0 0)", clipPath: "inset(50%)",
  whiteSpace: "nowrap", border: 0,
};

export interface LoadingRegionProps {
  /** The one sentence announced for this surface. One region per surface. */
  readonly label: string;
  readonly busy?: boolean;
  /**
   * Layout for the SHAPES. The decorative children are the region's own layout
   * children — the element that hides them from assistive tech is
   * `display: contents` — so flex, grid and gap set here reach them.
   */
  readonly style?: CSSProperties;
  readonly className?: string;
  readonly testId?: string;
  readonly children: ReactNode;
}

/**
 * The region's own box rules, applied BEFORE the caller's style so a caller can
 * still say otherwise. A region handed `width: 100%` and a padding was 100% of
 * its container PLUS that padding — exactly 40px wider than a phone viewport on
 * the public journey page, with the right-hand cards clipped. `border-box`
 * keeps the padding inside the width it was given, and `min-width: 0` lets the
 * region shrink as a flex or grid child instead of holding its content's width.
 */
const REGION_BOX: CSSProperties = { boxSizing: "border-box", minWidth: 0 };

/**
 * The wrapper every ghost renders inside: one polite announcement, `aria-busy`
 * for the pending state, and every shape hidden from the accessibility tree.
 */
export function LoadingRegion(props: LoadingRegionProps): JSX.Element {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy={props.busy !== false}
      className={props.className ? `${GHOST_CLASS} ${props.className}` : GHOST_CLASS}
      style={props.style ? { ...REGION_BOX, ...props.style } : REGION_BOX}
      data-testid={props.testId}
    >
      <GhostStyles />
      <span style={SR_ONLY}>{props.label}</span>
      {/* `display: contents`, so the SHAPES are laid out by the region and not
          by this element. Callers set flex/grid/gap on the region meaning it for
          the children; while this wrapper generated a box, it was the region's
          only layout child and every one of those declarations reached exactly
          one anonymous block. It stays, because it is what keeps the whole shape
          subtree out of the accessibility tree.

          Neither of the two siblings above joins that layout: the announcement
          is absolutely positioned, and a `<style>` element is `display: none`. */}
      <div aria-hidden="true" style={{ display: "contents" }}>{props.children}</div>
    </div>
  );
}

/**
 * One polite announcement for a surface whose ghosts are SPREAD THROUGH REAL
 * CHROME — the One Stop board draws shipped headings, a shipped search row and
 * a footer between them. A single `LoadingRegion` around such a page would
 * either hide that shipped markup from assistive tech or add a box to the
 * layout; this carries the sentence and nothing else, while `GHOST_CLASS` on
 * the regions the surface already renders scopes the shapes.
 */
export function LoadingAnnouncement(props: {
  readonly label: string;
  readonly busy?: boolean;
  readonly testId?: string;
}): JSX.Element {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy={props.busy !== false}
      style={SR_ONLY}
      data-testid={props.testId}
    >
      {props.label}
    </div>
  );
}

/** Radii of the surfaces these ghosts stand in for, named once so a ghost and
 *  its real counterpart cannot drift onto different corners. `control` is the
 *  DS's own control corner (6px), which is what the buttons, chips and fields
 *  these ghosts replace are drawn at — it read `--jrni-radius-sm` (4px, the
 *  checkbox inset) until 2026-09-24. */
export const GHOST_RADIUS = {
  card: "var(--jrni-radius-default)",
  panel: "var(--jrni-radius-md)",
  control: "var(--jrni-radius-control)",
} as const;

export interface SkeletonBoxProps {
  readonly width?: number | string;
  readonly height?: number | string;
  /**
   * The corner this ghost's real counterpart has.
   *
   * `SkeletonProps` is width/height/variant/shimmer — no class, no style, no
   * radius — and the component writes its own corner INLINE: the pill token for
   * `circle`, 999px for `text`, `--jrni-radius-sm` for `rect`. So the wrapper
   * carries the authored radius and clips, and the scoped rule in `GHOST_CSS`
   * makes the DS element inherit it.
   *
   * BOTH HALVES MATTER. The clip alone cannot un-round a corner, so an authored
   * `0` / `"0px"` rendered at `--jrni-radius-sm` — visible wherever a journey is
   * configured square. Inside a `LoadingRegion` (or any `GHOST_CLASS` scope)
   * the authored value is what renders; outside one, only the clip applies and
   * the DS corner shows through.
   *
   * A NUMBER IS A LENGTH IN PX, which is what the journey config actually holds
   * (`radiusScale`), so both spellings are accepted rather than only the one
   * the callers that stringify happen to send.
   */
  readonly radius?: number | string;
  readonly variant?: "text" | "rect" | "circle";
  /** `flex: 1 1 0` instead of a width. Never both — the two together overflow. */
  readonly grow?: boolean;
  readonly style?: CSSProperties;
}

export function SkeletonBox(props: SkeletonBoxProps): JSX.Element {
  const width = props.width ?? "100%";
  const height = props.height;
  /* Presence, not truthiness: `"0px"` is an authored square and must still get
     its wrapper. A radius that read as absent left the DS corner in place AND
     dropped the box the caller asked for. */
  const hasRadius = props.radius !== undefined && props.radius !== "";
  const wrapped = hasRadius || props.grow === true || props.style !== undefined;

  if (!wrapped) {
    return (
      <Skeleton
        width={width}
        {...(height !== undefined ? { height } : {})}
        variant={props.variant ?? "rect"}
      />
    );
  }

  /* The wrapper owns the box; the DS element fills it.
     `height` must be DEFINITE whenever a wrapper is used — the inner element
     is handed that same height, never a percentage resolved against an
     indefinite one, which is how a stretched or aspect-sized wrapper ended up
     around a 48px default bar. `display: flex` makes the inner element take
     the wrapper's full width. */
  return (
    <span
      aria-hidden="true"
      /* The hook for the inherit rule. Only set when a radius was authored, so
         an unwrapped-for-other-reasons box keeps the DS corner it asked for. */
      data-vg-ghost-radius={hasRadius ? "" : undefined}
      style={{
        display: "flex",
        alignItems: "stretch",
        ...(props.grow
          ? { flexGrow: 1, flexBasis: 0, minWidth: 0 }
          : { width: typeof width === "number" ? `${width}px` : width, flex: "none" }),
        ...(height !== undefined ? { height: typeof height === "number" ? `${height}px` : height } : {}),
        ...(hasRadius ? { borderRadius: props.radius, overflow: "hidden" } : {}),
        ...props.style,
      }}
    >
      <Skeleton
        width="100%"
        height={height !== undefined ? height : "100%"}
        variant={props.variant ?? "rect"}
      />
    </span>
  );
}

/* ── journey steps ──────────────────────────────────────────────────────── */

/** Media heights are DEFINITE so the inner DS element is sized, never a
 *  percentage against an indefinite box. 150px and 84px are authored stand-ins
 *  for the card banner and the list thumbnail; neither has been measured
 *  against a rendered card. */
const TILE_MEDIA_H = 150;
const ROW_MEDIA_H = 84;

/** A service or event tile: optional media, then title, meta and description.
 *  `radius` is the journey's own configured corner — the real cards take it
 *  from the theme, so a ghost on a fixed token would not match them. */
export function GhostServiceTile(props: {
  readonly i: number;
  readonly asRow?: boolean;
  readonly withMedia?: boolean;
  readonly radius?: number | string;
}): JSX.Element {
  const asRow = props.asRow === true;
  const radius = props.radius ?? GHOST_RADIUS.panel;
  return (
    <div
      aria-hidden="true"
      style={{
        display: "flex",
        flexDirection: asRow ? "row" : "column",
        alignItems: asRow ? "stretch" : undefined,
        overflow: "hidden",
        background: "var(--jrni-color-surface-card)",
        border: "1px solid var(--jrni-color-surface-border)",
        boxShadow: "var(--jrni-shadow-chrome)",
        borderRadius: radius,
      }}
    >
      {props.withMedia !== false ? (
        asRow
          ? <SkeletonBox width={84} height={ROW_MEDIA_H} style={{ flex: "none" }} />
          : <SkeletonBox width="100%" height={TILE_MEDIA_H} />
      ) : null}
      <div style={{ display: "flex", flexDirection: "column", gap: 6, padding: asRow ? "12px 14px" : "14px 16px 16px", minWidth: 0, flex: 1 }}>
        <SkeletonBox variant="text" width={["72%", "58%", "81%", "64%"][props.i % 4]} height={15} />
        <SkeletonBox variant="text" width="38%" height={13} />
        <SkeletonBox variant="text" width="92%" height={13} />
      </div>
    </div>
  );
}

/** A selectable row: service point, staff, ticket tier. */
export function GhostChoiceRow({ i, radius }: { readonly i: number; readonly radius?: number | string }): JSX.Element {
  return (
    <div
      aria-hidden="true"
      style={{
        display: "flex", alignItems: "center", gap: 12, padding: "12px 14px",
        background: "var(--jrni-color-surface-card)",
        border: "1px solid var(--jrni-color-surface-border)",
        borderRadius: radius ?? GHOST_RADIUS.panel,
      }}
    >
      <SkeletonBox variant="circle" width={36} height={36} />
      <span style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1, minWidth: 0 }}>
        <SkeletonBox variant="text" width={["62%", "48%", "71%"][i % 3]} height={14} />
        <SkeletonBox variant="text" width="34%" height={11} />
      </span>
    </div>
  );
}

/** The month grid: header, weekday row, five weeks of day cells. */
export function GhostCalendar({ radius }: { readonly radius?: number | string } = {}): JSX.Element {
  const control = radius ?? GHOST_RADIUS.control;
  return (
    <div aria-hidden="true" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <SkeletonBox variant="text" width={132} height={16} />
        <span style={{ display: "flex", gap: 8 }}>
          <SkeletonBox width={32} height={32} radius={control} />
          <SkeletonBox width={32} height={32} radius={control} />
        </span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: 6 }}>
        {Array.from({ length: 7 }, (_, i) => (
          <span key={`w${i}`} style={{ display: "flex", justifyContent: "center" }}>
            <SkeletonBox variant="text" width={22} height={10} />
          </span>
        ))}
        {/* Day cells are pill-shaped in the real month grid, not the control
            radius, so they keep 999px whatever the journey configures. */}
        {Array.from({ length: 35 }, (_, i) => (
          <SkeletonBox key={`d${i}`} width="100%" height={38} radius="999px" />
        ))}
      </div>
    </div>
  );
}

/** Slot chips, two groups, at a 44px touch height. */
export function GhostSlots({ count = 12, radius }: { readonly count?: number; readonly radius?: number | string }): JSX.Element {
  const per = Math.max(1, Math.round(count / 2));
  const control = radius ?? GHOST_RADIUS.control;
  return (
    <div aria-hidden="true" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {[0, 1].map((g) => (
        <div key={g} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <SkeletonBox variant="text" width={g === 0 ? 74 : 88} height={11} />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))", gap: 8 }}>
            {Array.from({ length: per }, (_, i) => (
              <SkeletonBox key={i} width="100%" height={44} radius={control} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Label over field, at a 44px control height. */
export function GhostFormFields({ count = 4, radius }: { readonly count?: number; readonly radius?: number | string }): JSX.Element {
  const control = radius ?? GHOST_RADIUS.control;
  return (
    <div aria-hidden="true" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
      {Array.from({ length: count }, (_, i) => (
        <span key={i} style={{ display: "flex", flexDirection: "column", gap: 6, ...(i >= count - 1 ? { gridColumn: "1 / -1" } : {}) }}>
          <SkeletonBox variant="text" width={["38%", "44%", "31%"][i % 3]} height={11} />
          <SkeletonBox width="100%" height={44} radius={control} />
        </span>
      ))}
    </div>
  );
}

/** A ticket or confirmation panel: headline, reference, aside. */
export function GhostTicketPanel(): JSX.Element {
  return (
    <div
      aria-hidden="true"
      style={{
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 18,
        padding: 14, borderRadius: "var(--jrni-radius-default)",
        background: "var(--jrni-color-surface-card-soft, var(--jrni-color-neutral-6))",
      }}
    >
      <span style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0, flex: 1 }}>
        <SkeletonBox variant="text" width={92} height={10} />
        <SkeletonBox variant="text" width="54%" height={20} />
        <SkeletonBox variant="text" width="38%" height={12} />
      </span>
      <span style={{ flex: "none", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
        <SkeletonBox variant="text" width={44} height={28} />
        <SkeletonBox variant="text" width={62} height={10} />
      </span>
    </div>
  );
}

export type GhostStep =
  | "service" | "location" | "staff" | "calendar" | "slots"
  | "details" | "questions" | "tickets" | "confirmation";

/** The step-shaped body a journey shows while that step's data is in flight.
 *  One component, so the wizard, the walk-in page and the one-page layout
 *  cannot drift apart. */
export function GhostJourneyStep(props: {
  readonly step: GhostStep;
  readonly asRow?: boolean;
  readonly withMedia?: boolean;
  readonly count?: number;
  /** The journey's configured corner, so the ghost matches the cards and
   *  controls it is standing in for rather than a fixed token. */
  readonly radius?: number | string;
  /**
   * The `service` step's grid, when the surface it stands in for has its own —
   * the caller hands over the SAME track list and gap its loaded grid uses, so
   * the ghost wraps at the widths the real cards wrap at. Omitted, the step
   * keeps its generic grid (what Walk-Ins renders); `asRow` still wins.
   */
  readonly columns?: string;
  readonly gap?: number | string;
}): JSX.Element {
  const { step, radius } = props;
  const n = props.count ?? (step === "service" ? 4 : 3);
  if (step === "service") {
    return (
      <div
        aria-hidden="true"
        style={{
          display: "grid", gap: props.gap ?? 10,
          gridTemplateColumns: props.asRow ? "minmax(0, 1fr)" : props.columns ?? "repeat(auto-fit, minmax(min(200px, 100%), 1fr))",
        }}
      >
        {Array.from({ length: n }, (_, i) => (
          <GhostServiceTile key={i} i={i} asRow={props.asRow} withMedia={props.withMedia} radius={radius} />
        ))}
      </div>
    );
  }
  if (step === "location" || step === "staff" || step === "tickets") {
    return (
      <div aria-hidden="true" style={{ display: "grid", gap: 10, gridTemplateColumns: "minmax(0, 1fr)" }}>
        {Array.from({ length: n }, (_, i) => <GhostChoiceRow key={i} i={i} radius={radius} />)}
      </div>
    );
  }
  if (step === "calendar") return <GhostCalendar radius={radius} />;
  if (step === "slots") return <GhostSlots count={props.count ?? 12} radius={radius} />;
  if (step === "confirmation") return <GhostTicketPanel />;
  return <GhostFormFields count={props.count ?? (step === "questions" ? 3 : 4)} radius={radius} />;
}

/** A whole journey step, headed and announced — what a standalone page shows
 *  before its first read lands. The heading is part of it so the real heading
 *  arrives into a space already held open for it. */
export function GhostJourneyBody(props: {
  readonly label: string;
  readonly step: GhostStep;
  readonly asRow?: boolean;
  readonly withMedia?: boolean;
  readonly count?: number;
  readonly radius?: number | string;
  readonly testId?: string;
}): JSX.Element {
  return (
    <LoadingRegion label={props.label} testId={props.testId} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <span style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <SkeletonBox variant="text" width="46%" height={19} />
        <SkeletonBox variant="text" width="72%" height={13} />
      </span>
      <GhostJourneyStep step={props.step} asRow={props.asRow} withMedia={props.withMedia} count={props.count} radius={props.radius} />
    </LoadingRegion>
  );
}

/** What the One Stop popup shows while the framed journey comes up. Stands in
 *  for a journey page rather than for a spinner, so the dialog is holding a
 *  page-sized space when the frame takes over. Readiness stays the frame's own
 *  handshake. */
export function GhostFramedJourney({ label }: { readonly label: string }): JSX.Element {
  return (
    <LoadingRegion
      label={label}
      testId="onestop-journey-popup-ghost"
      style={{ position: "absolute", inset: 0, overflow: "hidden", background: "var(--jrni-color-surface-canvas, #F6F7F9)" }}
    >
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <div
          style={{
            display: "flex", alignItems: "center", gap: 10, padding: "0 20px", height: 56, flex: "none",
            background: "var(--jrni-color-base-white, #FFFFFF)",
            borderBottom: "1px solid var(--jrni-color-surface-border, #D1D4DC)",
          }}
        >
          <SkeletonBox width={28} height={28} radius={GHOST_RADIUS.control} />
          <SkeletonBox variant="text" width={148} height={14} />
        </div>
        <div style={{ display: "flex", gap: 20, padding: "16px 24px 0", flex: "none" }}>
          {[54, 68, 72, 58].map((w, i) => <SkeletonBox key={i} variant="text" width={w} height={12} />)}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: "20px 24px", minHeight: 0, flex: 1 }}>
          <SkeletonBox variant="text" width="52%" height={19} />
          <GhostJourneyStep step="service" count={3} />
        </div>
      </div>
    </LoadingRegion>
  );
}

/** The narrow ghost a standalone host shows while it is still resolving which
 *  journey it serves, or re-establishing a handed-off session. Deliberately
 *  smaller than a step ghost: the page does not yet know what it will draw. */
export function GhostPageBoot({ label, testId }: { readonly label: string; readonly testId?: string }): JSX.Element {
  return (
    <LoadingRegion label={label} testId={testId} style={{ margin: "auto", width: "100%", maxWidth: 480, padding: "32px 24px" }}>
      <span style={{ display: "flex", flexDirection: "column", gap: 12, alignItems: "center" }}>
        <SkeletonBox variant="text" width="62%" height={18} />
        <SkeletonBox variant="text" width="84%" height={12} />
        <SkeletonBox variant="text" width="48%" height={12} />
      </span>
    </LoadingRegion>
  );
}
